import { partition } from "lodash-es";
import { FC } from "react";

import { showAddSectionModal } from "../helpers";

import { showRenameCourseModal } from "./AddOrRenameCourseModalUtils";
import { CourseOptionsMenu } from "./CourseOptionsMenu";

import { LoadingScreen } from "components/LoadingScreen";
import { AdminSectionListPanel } from "components/materials/home/admin/content/AdminSectionList";
import { CourseType } from "components/server-types";
import { t } from "i18n/i18n";
import { AccordionTitle } from "mds/components/AccordionTitle";
import { Banner } from "mds/components/Banner";
import { Button } from "mds/components/Button";
import { IconText } from "mds/components/IconText";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { AddPlusIcon, ChevronRightIcon, FolderIcon, TextBooksIcon } from "mds/icons";
import { useAppDispatch, useAppSelector } from "store/index";
import { courseGroupingSelectors, selectSortedTermSectionsByCourse } from "store/selectors";
import { localStateActions } from "store/slices/local";
import { useListSelector } from "store/store-hooks";
import { ADMIN_COURSE_EVENT_PREFIX } from "utils/amplitude";
import { getCourseDisplayTitle } from "utils/course";

type SectionDetailTermViewProps = {
  course: CourseType;
  canEdit: boolean;
};

export const SectionDetailTermView: FC<SectionDetailTermViewProps> = ({ course, canEdit }) => {
  const dispatch = useAppDispatch();
  const termSections = useListSelector((s) => selectSortedTermSectionsByCourse(s, course?.id));
  const visibleTermSections = termSections.filter((term) => term.sections.length > 0);
  const [archivedTermSections, activeTermSections] = partition(
    visibleTermSections,
    (term) => term.archived_at,
  );
  const grouping = useAppSelector((s) => courseGroupingSelectors.selectById(s, course.grouping_id));

  const handleEnableEditMode = () => {
    dispatch(localStateActions.setIsEditMode(true));
  };

  if (!course) {
    return <LoadingScreen>{t("home.settings.no_sections")}</LoadingScreen>;
  }

  const handleAddSection = () =>
    showAddSectionModal({
      defaultCourseId: course.id,
      eventName: `${ADMIN_COURSE_EVENT_PREFIX}Add Section`,
    });

  return (
    <div className="px-0 py-3 md:px-7">
      <h2 className="mb-3 flex items-start justify-between">
        <div className="flex items-center gap-2">
          {grouping && (
            <div className="flex items-center gap-2">
              <FolderIcon className="mb-[3px] text-blue" size="l" />
              <span className="text-black-tint-20">{grouping.title}</span>
              <ChevronRightIcon />
            </div>
          )}

          <div className="flex items-center gap-2">
            <TextBooksIcon className="mt-[1px] text-blue" size="l" />
            <div className="h2">{getCourseDisplayTitle(course)}</div>
          </div>
        </div>

        {canEdit && (
          <div className="flex items-center gap-2">
            <Button
              eventName={`${ADMIN_COURSE_EVENT_PREFIX}Add Section`}
              kind="gray"
              size="s"
              onClick={handleAddSection}
            >
              <IconText iconStart={<AddPlusIcon />} text={t("common.add.section")} />
            </Button>

            <CourseOptionsMenu
              course={course}
              kind="gray"
              onRename={() => showRenameCourseModal(course)}
            />
          </div>
        )}
      </h2>

      {!visibleTermSections.length ? (
        <Banner
          buttonChildren={
            canEdit ? (
              <SquareButton
                eventName={`${ADMIN_COURSE_EVENT_PREFIX}Add Section`}
                kind="accent"
                size="s"
                onClick={handleAddSection}
              >
                <IconText iconStart={<AddPlusIcon />} text={t("common.add.section")} />
              </SquareButton>
            ) : (
              <SquareButton
                eventName={`${ADMIN_COURSE_EVENT_PREFIX}Enable Edit Mode`}
                kind="accent"
                size="s"
                onClick={handleEnableEditMode}
              >
                {t("warning.enable_edit_mode")}
              </SquareButton>
            )
          }
          kind="info"
        >
          {canEdit
            ? t("home.courses.empty_course_banner_text")
            : t("home.courses.empty_course_banner_text_no_edit")}
        </Banner>
      ) : (
        <>
          <ul>
            {activeTermSections.map((term) => (
              <li className="course-panel ml-2" key={term.id}>
                <AdminSectionListPanel
                  canEdit={canEdit}
                  eventPrefix={ADMIN_COURSE_EVENT_PREFIX}
                  eventProperties={{ course: course.code, term: term.title }}
                  sections={term.sections}
                  title={term.title}
                />
              </li>
            ))}
          </ul>

          {archivedTermSections.length > 0 && (
            <AccordionTitle
              headerClassName="ml-2 w-[calc(100%-12px)] justify-between border-b border-black-tint-90 pr-4"
              iconDirection="end"
              title={t("home.terms.archived_terms")}
            >
              <ul>
                {archivedTermSections.map((term) => (
                  <li className="course-panel ml-2" key={term.id}>
                    <AdminSectionListPanel
                      canEdit={canEdit}
                      eventPrefix={ADMIN_COURSE_EVENT_PREFIX}
                      eventProperties={{ course: course.code, term: term.title }}
                      sections={term.sections}
                      title={term.title}
                      isDisabled
                    />
                  </li>
                ))}
              </ul>
            </AccordionTitle>
          )}
        </>
      )}
    </div>
  );
};
